import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Standardized Button Component
 * Primary, Secondary, Tertiary variants
 * Consistent styling across admin and user flows
 * Supports light and dark themes
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium font-sans ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary - Main action button
        primary:
          'bg-indigo-600 dark:bg-indigo-500 text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:ring-indigo-500',
        // Secondary - Secondary action button
        secondary:
          'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-gray-500',
        // Tertiary - Subtle action button
        tertiary:
          'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-300 dark:border-gray-600 focus:ring-gray-500',
        // Destructive - Delete/danger actions
        destructive:
          'bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 focus:ring-red-500',
        // Outline - Outlined button
        outline:
          'border border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-gray-500',
        // Ghost - Minimal button
        ghost: 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
        // Link - Link-style button
        link: 'text-indigo-600 dark:text-indigo-400 underline-offset-4 hover:underline',
        // Brand variants (for user flow only - feature toggle applicable)
        brand:
          'bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(var(--brand-light-green))] focus:ring-[hsl(var(--brand-green))]',
        'brand-blue':
          'bg-[hsl(var(--brand-blue))] text-white hover:bg-[hsl(var(--brand-light-blue))] focus:ring-[hsl(var(--brand-blue))]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export const Button = React.forwardRef(function Button(
  { className, variant, size, asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});

Button.displayName = 'Button';

// Icon Button - Square button for icons only
export const IconButton = React.forwardRef(function IconButton(
  { className, variant = 'ghost', size = 'icon', ...props },
  ref
) {
  return (
    <Button ref={ref} variant={variant} size={size} className={cn('p-0', className)} {...props} />
  );
});

IconButton.displayName = 'IconButton';

export default Button;
